import React, { useState, useEffect } from 'react';
import { LocationInput } from '../types';
import { ArrowLeft, CircleHelp, Clock, MapPin, Minus, Plus, Send, Bike, User, X } from 'lucide-react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { backendApi } from '../services/backendApi';
import { localStorageUtils } from '../utils/localStorage';
import { OSDORP_BOUNDS, OSDORP_CENTER } from '../config/osdorpArea';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MapExplainerModal from '../components/MapExplainerModal';
import wijkGeoJson from '../data/json/wijk_geojson_osdpm.json';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface RouteResultsPageProps {
  startLocation: LocationInput;
  endLocation: LocationInput;
  routeCoordinates?: [number, number][]; // Optional: if provided, use these instead of generating new route
  requestId?: number; // Request ID from backend route init
  routeStats?: {
    total_distance?: number;
    total_crossings?: number;
    distance_sidewalk?: number;
    distance_bikelane?: number;
    total_duration?: number;
    time_sidewalk?: number;
    time_bikelane?: number;
  };
  onBack: () => void;
  onStartRoute: (routeCoordinates: [number, number][]) => void;
}

interface RouteSegment {
  type: 'crossing' | 'bikepath' | 'sidewalk';
  duration: number; // in minutes
  distance: number; // in meters
}

interface RouteData {
  totalDuration: number; // in minutes
  totalDistance: number; // in meters
  segments: RouteSegment[];
  coordinates: [number, number][]; // Route polyline coordinates
}

const RouteResultsPage: React.FC<RouteResultsPageProps> = ({
  startLocation,
  endLocation,
  routeCoordinates: providedRouteCoordinates,
  requestId,
  routeStats,
  onBack,
  onStartRoute
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  const [adjustments, setAdjustments] = useState({
    crossings: 0,
    bikepath: 0,
    sidewalk: 0
  });
  const [comparisonRoute, setComparisonRoute] = useState<{
    coordinates: [number, number][];
    totalDuration: number; // in minutes
    totalCrossings: number; // number of crossings in the old route
    totalDistance: number; // distance in meters
    bikepath: number; // bikepath duration in minutes
    sidewalk: number; // sidewalk duration in minutes
  } | null>(null);
  const [showingPreviousRoute, setShowingPreviousRoute] = useState(false);
  /** Comparative UI only after optimize + compare returns a previous route geometry */
  const [hasComparableAlternate, setHasComparableAlternate] = useState(false);
  const [isResettingRoute, setIsResettingRoute] = useState(false);

  // Store original route data to switch back
  const [originalRouteData, setOriginalRouteData] = useState<RouteData | null>(null);
  const [showMapExplainer, setShowMapExplainer] = useState(false);

  useEffect(() => {
    setComparisonRoute(null);
    setHasComparableAlternate(false);
    setShowingPreviousRoute(false);
  }, [requestId]);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in meters
  };

  // Find the minimum distance from a point to any point on a route
  const minDistanceToRoute = (point: [number, number], route: [number, number][]): number => {
    if (route.length === 0) return Infinity;
    let minDist = Infinity;
    for (const routePoint of route) {
      const dist = calculateDistance(point[0], point[1], routePoint[0], routePoint[1]);
      if (dist < minDist) {
        minDist = dist;
      }
    }
    return minDist;
  };

  // Get only the segments of the old route that differ from the new route
  // Returns segments where points are more than threshold distance from the new route
  const getRouteDifference = (
    oldRoute: [number, number][],
    newRoute: [number, number][],
    thresholdMeters: number = 10 // Points within 10 meters are considered "overlapping"
  ): [number, number][][] => {
    if (!oldRoute || oldRoute.length === 0 || !newRoute || newRoute.length === 0) {
      return [oldRoute];
    }

    const segments: [number, number][][] = [];
    let currentSegment: [number, number][] = [];

    for (let i = 0; i < oldRoute.length; i++) {
      const point = oldRoute[i];
      const distance = minDistanceToRoute(point, newRoute);

      if (distance > thresholdMeters) {
        // Point is far from new route - add to current segment
        currentSegment.push(point);
      } else {
        // Point is close to new route - end current segment if it exists
        if (currentSegment.length > 0) {
          segments.push(currentSegment);
          currentSegment = [];
        }
      }
    }

    // Add any remaining segment
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }

    return segments.filter(seg => seg.length > 1); // Only return segments with at least 2 points
  };
  
  // Store original route data when first loaded
  useEffect(() => {
    if (routeData && !originalRouteData) {
      setOriginalRouteData(routeData);
    }
  }, [routeData, originalRouteData]);

  // Update adjustments when route data is loaded from backend
  useEffect(() => {
    if (routeData) {
      // Find segment durations from route data
      const crossingSegment = routeData.segments.find(s => s.type === 'crossing');
      const bikepathSegment = routeData.segments.find(s => s.type === 'bikepath');
      const sidewalkSegment = routeData.segments.find(s => s.type === 'sidewalk');
      
      setAdjustments({
        crossings: crossingSegment?.duration || 0,
        bikepath: bikepathSegment?.duration || 0,
        sidewalk: sidewalkSegment?.duration || 0
      });
    }
  }, [routeData]);
  
  // Handler to switch between current and previous route
  const handleTogglePreviousRoute = () => {
    if (!comparisonRoute || !originalRouteData) return;
    
    if (showingPreviousRoute) {
      // Switch back to current route
      setRouteData(originalRouteData);
      setShowingPreviousRoute(false);
    } else {
      // Switch to previous route
      const previousRouteData: RouteData = {
        totalDuration: comparisonRoute.totalDuration,
        totalDistance: comparisonRoute.totalDistance,
        segments: [
          { type: 'crossing', duration: comparisonRoute.totalCrossings, distance: 0 },
          { type: 'bikepath', duration: comparisonRoute.bikepath, distance: 0 },
          { type: 'sidewalk', duration: comparisonRoute.sidewalk, distance: 0 }
        ],
        coordinates: comparisonRoute.coordinates
      };
      setRouteData(previousRouteData);
      setShowingPreviousRoute(true);
    }
  };

  // Load route data - use provided coordinates if available, otherwise backend only (no OSRM fallback)
  useEffect(() => {
    const loadRoute = async () => {
      setIsLoading(true);
      setLoadError(false);
      setRouteData(null);

      try {
        // If route coordinates are already provided (from backend), use them directly.
        // IMPORTANT: We do NOT call /routes/init again here to avoid triggering
        // additional backend work for the same route. Instead, we prefer any
        // statistics already returned from the initial init call (routeStats).
        if (providedRouteCoordinates && providedRouteCoordinates.length > 0) {
          console.log('Using provided route coordinates from backend:', providedRouteCoordinates.length, 'points');

          // If we have detailed stats from backend, use them; otherwise estimate.
          let totalDistance = 0;
          let totalDurationMinutes: number;
          let totalCrossings = 0;
          let distanceSidewalk: number;
          let distanceBikelane: number;
          let timeSidewalkMinutes: number;
          let timeBikelaneMinutes: number;

          if (routeStats && routeStats.total_distance && routeStats.total_duration) {
            totalDistance = routeStats.total_distance;
            totalDurationMinutes = Math.round(routeStats.total_duration / 60);
            totalCrossings = routeStats.total_crossings || 0;
            distanceSidewalk = routeStats.distance_sidewalk || 0;
            distanceBikelane = routeStats.distance_bikelane || 0;
            timeSidewalkMinutes = Math.round((routeStats.time_sidewalk || 0) / 60);
            timeBikelaneMinutes = Math.round((routeStats.time_bikelane || 0) / 60);
          } else {
            // Calculate distance and duration from provided coordinates only
            for (let i = 1; i < providedRouteCoordinates.length; i++) {
              const [prevLat, prevLng] = providedRouteCoordinates[i - 1];
              const [currLat, currLng] = providedRouteCoordinates[i];
              totalDistance += calculateDistance(prevLat, prevLng, currLat, currLng);
            }
            totalDistance = Math.round(totalDistance);
            totalDurationMinutes = Math.round(totalDistance / 1.4 / 60); // Walking speed ~1.4 m/s

            // Rough split for segments when stats are missing
            distanceSidewalk = Math.round(totalDistance * 0.2);
            distanceBikelane = Math.round(totalDistance * 0.8);
            timeSidewalkMinutes = Math.round(totalDurationMinutes * 0.2);
            timeBikelaneMinutes = Math.round(totalDurationMinutes * 0.8);
          }
          
          const routeData: RouteData = {
            totalDuration: totalDurationMinutes,
            totalDistance: totalDistance,
            segments: [
              { type: 'crossing', duration: totalCrossings, distance: 0 },
              { type: 'bikepath', duration: timeBikelaneMinutes, distance: distanceBikelane },
              { type: 'sidewalk', duration: timeSidewalkMinutes, distance: distanceSidewalk }
            ],
            coordinates: providedRouteCoordinates
          };
          
          setRouteData(routeData);
          return;
        }

        if (!startLocation.coordinates || !endLocation.coordinates) {
          setLoadError(true);
          return;
        }

        const userId = localStorageUtils.getUserId();
        if (!userId) {
          setLoadError(true);
          return;
        }

        try {
          console.log('No provided route coordinates, attempting backend route generation...');
          const backendResponse = await backendApi.initRoute(userId, {
            start_lat: startLocation.coordinates.lat,
            start_lon: startLocation.coordinates.lng,
            end_lat: endLocation.coordinates.lat,
            end_lon: endLocation.coordinates.lng,
            start_address: startLocation.address,
            end_address: endLocation.address,
          });

          let routeCoordinates: [number, number][] | undefined = undefined;
          if (backendResponse?.result?.geometry?.coordinates) {
            const geoJsonCoords = backendResponse.result.geometry.coordinates;
            routeCoordinates = geoJsonCoords.map((coord: [number, number]) => [coord[1], coord[0]]);
          } else if (backendResponse?.coordinates) {
            routeCoordinates = backendResponse.coordinates;
          } else if (backendResponse?.route?.coordinates) {
            routeCoordinates = backendResponse.route.coordinates;
          }

          if (routeCoordinates && routeCoordinates.length > 0) {
            const totalDistance = backendResponse?.total_distance || 0;
            const totalDuration = backendResponse?.total_duration || 0;
            const totalCrossings = backendResponse?.total_crossings || 0;
            const distanceSidewalk = backendResponse?.distance_sidewalk || 0;
            const distanceBikelane = backendResponse?.distance_bikelane || 0;
            const timeSidewalk = backendResponse?.time_sidewalk || 0;
            const timeBikelane = backendResponse?.time_bikelane || 0;

            const routeData: RouteData = {
              totalDuration: Math.round(totalDuration / 60),
              totalDistance: totalDistance,
              segments: [
                { type: 'crossing', duration: totalCrossings, distance: 0 },
                { type: 'bikepath', duration: Math.round(timeBikelane / 60), distance: distanceBikelane },
                { type: 'sidewalk', duration: Math.round(timeSidewalk / 60), distance: distanceSidewalk }
              ],
              coordinates: routeCoordinates
            };

            setRouteData(routeData);
            return;
          }
        } catch (backendError) {
          console.warn('Backend route generation failed in RouteResultsPage:', backendError);
        }

        setLoadError(true);
      } catch (error) {
        console.error('Route loading error:', error);
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadRoute();
  }, [startLocation, endLocation, providedRouteCoordinates, routeStats]);

  // Helper function to load comparison route
  const loadComparisonRoute = async (routeRequestId?: number, excludeRouteId?: number): Promise<boolean> => {
    const idToUse = routeRequestId || requestId;

    if (!idToUse || idToUse === 0) {
      setComparisonRoute(null);
      return false;
    }

    const userId = localStorageUtils.getUserId();
    if (!userId) {
      return false;
    }

    try {
      console.log('Loading comparison route for requestId:', idToUse, 'excluding routeId:', excludeRouteId);
      const comparisonData = await backendApi.compareRoute(userId, idToUse);
      
      if (comparisonData && comparisonData.length > 0) {
        // Get the previous route (not the current one)
        // The compare endpoint returns routes sorted by iteration_index (newest first)
        // We want to exclude the current route and get the previous one
        let oldRoute = comparisonData[0];
        
        // If we have a route ID to exclude (current optimized route), filter it out
        if (excludeRouteId) {
          const filteredRoutes = comparisonData.filter(r => r.id !== excludeRouteId);
          if (filteredRoutes.length > 0) {
            // Get the route with the highest iteration_index from the filtered list
            oldRoute = filteredRoutes.sort((a, b) => 
              (b.iteration_index || 0) - (a.iteration_index || 0)
            )[0];
          } else {
            // If all routes were filtered out, fall back to second route
            oldRoute = comparisonData[1] || comparisonData[0];
          }
        } else if (comparisonData.length > 1) {
          // If no exclude ID, get the second route (previous iteration)
          const sortedRoutes = [...comparisonData].sort((a, b) => 
            (b.iteration_index || 0) - (a.iteration_index || 0)
          );
          oldRoute = sortedRoutes[1] || sortedRoutes[0];
        }
        
        console.log('Selected comparison route:', {
          id: oldRoute.id,
          iteration_index: oldRoute.iteration_index,
          total_routes: comparisonData.length,
          excluded_route_id: excludeRouteId
        });
        
        // Parse route_geometry_geojson if it exists
        let oldRouteCoordinates: [number, number][] = [];
        
        if (oldRoute.route_geometry_geojson) {
          try {
            const geoJson = typeof oldRoute.route_geometry_geojson === 'string' 
              ? JSON.parse(oldRoute.route_geometry_geojson)
              : oldRoute.route_geometry_geojson;
            
            // Handle both LineString (direct coordinates) and Feature (geometry.coordinates)
            let coordinates: [number, number][] | undefined;
            
            if (geoJson?.type === 'LineString' && geoJson?.coordinates) {
              // Direct LineString format: { type: "LineString", coordinates: [...] }
              coordinates = geoJson.coordinates;
            } else if (geoJson?.geometry?.type === 'LineString' && geoJson?.geometry?.coordinates) {
              // Feature format: { geometry: { type: "LineString", coordinates: [...] } }
              coordinates = geoJson.geometry.coordinates;
            } else if (geoJson?.coordinates) {
              // Fallback: try direct coordinates property
              coordinates = geoJson.coordinates;
            }
            
            if (coordinates && Array.isArray(coordinates) && coordinates.length > 0) {
              // Convert from [lng, lat] to [lat, lng] for Leaflet
              oldRouteCoordinates = coordinates.map(
                (coord: [number, number]) => [coord[1], coord[0]]
              );
              console.log('Parsed comparison route coordinates:', oldRouteCoordinates.length, 'points');
            } else {
              console.warn('No valid coordinates found in route_geometry_geojson:', geoJson);
            }
          } catch (parseError) {
            console.warn('Failed to parse route_geometry_geojson:', parseError, oldRoute.route_geometry_geojson);
          }
        } else {
          console.warn('No route_geometry_geojson in comparison route data');
        }

        if (oldRouteCoordinates.length > 0) {
          const bikepathDuration = Math.round((oldRoute.time_bikelane || 0) / 60);
          const sidewalkDuration = Math.round((oldRoute.time_sidewalk || 0) / 60);
          
          setComparisonRoute({
            coordinates: oldRouteCoordinates,
            totalDuration: Math.round((oldRoute.total_duration || 0) / 60), // Convert seconds to minutes
            totalCrossings: oldRoute.total_crossings || 0,
            totalDistance: oldRoute.total_distance || 0,
            bikepath: bikepathDuration,
            sidewalk: sidewalkDuration
          });
          console.log('Comparison route loaded:', {
            coordinates: oldRouteCoordinates.length,
            totalDuration: Math.round((oldRoute.total_duration || 0) / 60),
            totalCrossings: oldRoute.total_crossings || 0,
            totalDistance: oldRoute.total_distance || 0
          });
          return true;
        }
      }
      setComparisonRoute(null);
      return false;
    } catch (error) {
      console.warn('Failed to load comparison route:', error);
      setComparisonRoute(null);
      return false;
    }
  };

  const handleAdjustment = async (type: keyof typeof adjustments, delta: number) => {
    // Check if we have requestId and userId for optimization
    if (!requestId || requestId === 0) {
      // If no requestId, just update local state (fallback behavior)
      setAdjustments(prev => ({
        ...prev,
        [type]: Math.max(0, prev[type] + delta)
      }));
      return;
    }

    const userId = localStorageUtils.getUserId();
    if (!userId) {
      console.warn('Cannot optimize route: missing userId');
      return;
    }

    // Map adjustment type to objective value
    // 0=length, 1=crossing, 2=walk, 3=bike
    const objectiveMap: Record<keyof typeof adjustments, number> = {
      crossings: 1,
      sidewalk: 2,
      bikepath: 3,
    };

    const objective = objectiveMap[type];
    const direction = delta > 0 ? '+' : '-';

    setIsOptimizing(true);

    try {
      console.log('Optimizing route:', { objective, direction, type });
      const optimizeResponse = await backendApi.optimizeRoute(userId, requestId, {
        objective,
        direction,
      });

      // Extract updated route from optimization response
      if (optimizeResponse?.result?.geometry?.coordinates) {
        const geoJsonCoords = optimizeResponse.result.geometry.coordinates;
        const routeCoordinates = geoJsonCoords.map((coord: [number, number]) => [coord[1], coord[0]]);

        // Extract updated route statistics from response
        const totalDistance = optimizeResponse?.total_distance || routeData?.totalDistance || 0;
        const totalDuration = optimizeResponse?.total_duration || 0;
        const totalCrossings = optimizeResponse?.total_crossings || 0;
        const distanceSidewalk = optimizeResponse?.distance_sidewalk || 0;
        const distanceBikelane = optimizeResponse?.distance_bikelane || 0;
        const timeSidewalk = optimizeResponse?.time_sidewalk || 0;
        const timeBikelane = optimizeResponse?.time_bikelane || 0;
        const generatedRouteId = optimizeResponse?.generated_route_id;

        const updatedRouteData: RouteData = {
          totalDuration: Math.round(totalDuration / 60),
          totalDistance: totalDistance,
          segments: [
            { type: 'crossing', duration: totalCrossings, distance: 0 },
            { type: 'bikepath', duration: Math.round(timeBikelane / 60), distance: distanceBikelane },
            { type: 'sidewalk', duration: Math.round(timeSidewalk / 60), distance: distanceSidewalk }
          ],
          coordinates: routeCoordinates
        };

        // The current routeData becomes the previous route, and the new optimized route becomes current
        // Save the current route as the previous route before updating
        const previousRouteData = routeData;
        
        // Update route data with optimized route (new current route)
        setRouteData(updatedRouteData);
        // Update original route data to the new optimized route (it becomes the new current route)
        setOriginalRouteData(updatedRouteData);
        // Reset showing previous route state since we're now viewing the new optimized route
        setShowingPreviousRoute(false);
        
        // Update adjustments based on new route data
        setAdjustments({
          crossings: totalCrossings,
          bikepath: Math.round(timeBikelane / 60),
          sidewalk: Math.round(timeSidewalk / 60)
        });

        console.log('Route optimized successfully:', {
          totalDistance,
          totalDuration: updatedRouteData.totalDuration,
          totalCrossings,
          coordinates: routeCoordinates.length,
          previousRoute: previousRouteData ? 'exists' : 'none'
        });

        // Load comparison route to show the previous route (the one that was current before optimization)
        // Use the request_id from the optimization response, or fallback to current requestId
        const comparisonRequestId = optimizeResponse?.request_id || requestId;
        if (comparisonRequestId && comparisonRequestId !== 0) {
          console.log('Loading comparison route after optimization...', {
            requestId: comparisonRequestId,
            generatedRouteId: generatedRouteId
          });
          // Pass the current route ID to exclude it from comparison
          // This will get the route that was current before this optimization
          // Wrap in try-catch to prevent unhandled promise rejection
          try {
            const loaded = await loadComparisonRoute(comparisonRequestId, generatedRouteId);
            if (loaded) {
              setHasComparableAlternate(true);
            }
          } catch (comparisonError) {
            console.warn('Failed to load comparison route after optimization:', comparisonError);
            // Don't throw - this is not critical for the optimization flow
          }
        }
      } else {
        console.warn('Optimization response missing route coordinates:', optimizeResponse);
        // Still update adjustments even if coordinates are missing
    setAdjustments(prev => ({
      ...prev,
      [type]: Math.max(0, prev[type] + delta)
    }));
      }
    } catch (error) {
      console.error('Failed to optimize route:', error);
      // Revert adjustment on error
      setAdjustments(prev => ({
        ...prev,
        [type]: Math.max(0, prev[type] - delta)
      }));
    } finally {
      setIsOptimizing(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    return `${minutes} min`;
  };

  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  if (isLoading) {
    return (
      <div className="route-results-page">
        <div className="route-loading">
          <div className="loading-spinner"></div>
          <p className="loading-message">{t('status.calculating')}</p>
        </div>
      </div>
    );
  }

  if (loadError || !routeData) {
    return (
      <div className="route-results-page">
        <div className="route-error">
          <p>{t('route.unavailable')}</p>
          <button className="btn btn-primary" onClick={onBack}>
            {t('button.back')}
          </button>
        </div>
      </div>
    );
  }

  const handleBackWithReset = async () => {
    // Best-effort: do not block navigation if IDs are missing or request fails
    const userId = localStorageUtils.getUserId();
    if (!userId || !requestId) {
      onBack();
      return;
    }

    setIsResettingRoute(true);
    try {
      await backendApi.resetRoute(userId, requestId);
    } catch (error) {
      console.warn('Failed to reset route:', error);
    } finally {
      setIsResettingRoute(false);
      onBack();
    }
  };

  return (
    <div className="route-results-page">
      <LanguageSwitcher />
      {/* Header with close button */}
      <div className="route-header">
        <button className="back-button" onClick={handleBackWithReset} disabled={isResettingRoute}>
          <X size={36} />
        </button>
      </div>

      {/* Map */}
      <div className="route-map">
        <button
          type="button"
          className="map-section-info-btn"
          onClick={() => setShowMapExplainer(true)}
          aria-label={t('mapExplainer.open')}
        >
          <CircleHelp size={26} strokeWidth={2} aria-hidden />
        </button>
        <MapContainer
          center={routeData.coordinates.length > 0 ? routeData.coordinates[0] : OSDORP_CENTER}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          maxBounds={OSDORP_BOUNDS}
          maxBoundsViscosity={1.0}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {/* Osdorp-Midden neighborhood outline */}
          <GeoJSON
            data={wijkGeoJson as any}
            style={() => ({
              color: '#FF6B00',
              weight: 3,
              fillOpacity: 0,
              dashArray: '5, 5',
            })}
            onEachFeature={(feature, layer) => {
              if (feature.properties && feature.properties.Wijk) {
                layer.bindPopup(`<strong>${feature.properties.Wijk}</strong>`);
              }
            }}
          />
          
          {/* When viewing current route: show current route in blue and differences from previous route in orange */}
          {!showingPreviousRoute && (
            <>
              {/* Comparison route differences (orange) - only parts that differ */}
              {hasComparableAlternate &&
                comparisonRoute &&
                comparisonRoute.coordinates.length > 0 &&
                routeData &&
                routeData.coordinates.length > 0 && (
                <>
                  {getRouteDifference(comparisonRoute.coordinates, routeData.coordinates).map((segment, index) => (
                    <Polyline
                      key={`comparison-segment-${index}`}
                      positions={segment}
                      color="#FF6B00"
                      weight={5}
                      opacity={0.8}
                      dashArray="10, 5"
                    />
                  ))}
                </>
              )}
              
              {/* Current route (blue) */}
              <Polyline
                positions={routeData.coordinates}
                color="#007bff"
                weight={4}
                opacity={0.8}
              />
            </>
          )}
          
          {/* When viewing previous route: show previous route in orange and current route in blue */}
          {showingPreviousRoute && originalRouteData && originalRouteData.coordinates.length > 0 && (
            <>
              {/* Previous route (orange dashed) */}
              <Polyline
                positions={routeData.coordinates}
                color="#FF6B00"
                weight={5}
                opacity={0.8}
                dashArray="10, 5"
              />
              
              {/* Current route (blue) */}
              <Polyline
                positions={originalRouteData.coordinates}
                color="#007bff"
                weight={4}
                opacity={0.8}
              />
            </>
          )}
          
          {/* Start marker */}
          {routeData.coordinates.length > 0 && (
            <Marker position={routeData.coordinates[0]}>
              <Popup>
                <div>
                  <strong>{t('routeResults.start')}</strong><br />
                  {startLocation.address}
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* End marker */}
          {routeData.coordinates.length > 0 && (
            <Marker position={routeData.coordinates[routeData.coordinates.length - 1]}>
              <Popup>
                <div>
                  <strong>{t('routeResults.destination')}</strong><br />
                  {endLocation.address}
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Route Summary */}
      <div className="route-summary">
        <div className="route-time-distance">
          <Clock size={24} />
          <span className="time-distance-text">
            {formatDuration(routeData.totalDuration)} ({formatDistance(routeData.totalDistance)})
          </span>
        </div>
        
        {/* Duration comparison with old route - only show when viewing current route */}
        {!showingPreviousRoute &&
          hasComparableAlternate &&
          comparisonRoute &&
          comparisonRoute.totalDuration > 0 &&
          originalRouteData && (
          <div className="route-comparison">
            {originalRouteData.totalDuration < comparisonRoute.totalDuration ? (
              <div className="comparison-item positive">
                <Clock size={16} />
                <span>
                  {formatDuration(comparisonRoute.totalDuration - originalRouteData.totalDuration)} {t('routeResults.faster') || 'faster'} than previous route
                </span>
              </div>
            ) : originalRouteData.totalDuration > comparisonRoute.totalDuration ? (
              <div className="comparison-item negative">
                <Clock size={16} />
                <span>
                  {formatDuration(originalRouteData.totalDuration - comparisonRoute.totalDuration)} {t('routeResults.slower') || 'slower'} than previous route
                </span>
              </div>
            ) : (
              <div className="comparison-item neutral">
                <Clock size={16} />
                <span>
                  {t('routeResults.sameDuration') || 'Same duration as previous route'}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Show indicator when viewing previous route */}
        {showingPreviousRoute && (
          <div className="route-comparison">
            <div className="comparison-item neutral">
              <Clock size={16} />
              <span>
                {t('routeResults.viewingPreviousRoute') || 'Viewing previous route'}
              </span>
            </div>
          </div>
        )}
        
        {/* Route adjustments - only show differences when comparison route exists and viewing current route */}
        {!showingPreviousRoute &&
          hasComparableAlternate &&
          comparisonRoute &&
          comparisonRoute.totalCrossings !== undefined &&
          originalRouteData && (
          (() => {
            const currentCrossings = originalRouteData.segments.find(s => s.type === 'crossing')?.duration || 0;
            const crossingsDiff = comparisonRoute.totalCrossings - currentCrossings;
            if (crossingsDiff === 0) return null;
            
            return (
              <div className="route-adjustments">
                <div className={`adjustment-item ${crossingsDiff > 0 ? 'positive' : 'negative'}`}>
                  {crossingsDiff > 0 ? <Minus size={16} /> : <Plus size={16} />}
                  <span>
                    {crossingsDiff > 0 ? '-' : '+'}{Math.abs(crossingsDiff)} {t('routeResults.crossings')}
                  </span>
                </div>
              </div>
            );
          })()
        )}
      </div>

      {/* Route Details */}
      <div className="route-details">
        <div className="route-segment">
          <div className="segment-icon">
            <MapPin size={20} />
          </div>
          <div className="segment-info">
            <span className="segment-label">{t('routeResults.crossingsLabel')}</span>
          </div>
          <div className="route-segment-stepper">
            <button
              type="button"
              className="route-segment-stepper-btn"
              onClick={() => handleAdjustment('crossings', -1)}
              disabled={isOptimizing}
              aria-label={t('routeResults.decreaseCrossings')}
            >
              <Minus size={20} strokeWidth={2.5} />
            </button>
            <span className="route-segment-stepper-value">{adjustments.crossings}</span>
            <button
              type="button"
              className="route-segment-stepper-btn"
              onClick={() => handleAdjustment('crossings', 1)}
              disabled={isOptimizing}
              aria-label={t('routeResults.increaseCrossings')}
            >
              <Plus size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="route-segment">
          <div className="segment-icon">
            <Bike size={20} />
          </div>
          <div className="segment-info">
            <span className="segment-label">
              {t('routeResults.bikepathLabel')}
              {routeData.segments[1]?.distance
                ? ` (${formatDistance(routeData.segments[1].distance)})`
                : ''}
            </span>
          </div>
          <div className="route-segment-stepper">
            <button
              type="button"
              className="route-segment-stepper-btn"
              onClick={() => handleAdjustment('bikepath', -1)}
              disabled={isOptimizing}
              aria-label={t('routeResults.decreaseBikepath')}
            >
              <Minus size={20} strokeWidth={2.5} />
            </button>
            <span className="route-segment-stepper-value">{formatDuration(adjustments.bikepath)}</span>
            <button
              type="button"
              className="route-segment-stepper-btn"
              onClick={() => handleAdjustment('bikepath', 1)}
              disabled={isOptimizing}
              aria-label={t('routeResults.increaseBikepath')}
            >
              <Plus size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="route-segment">
          <div className="segment-icon">
            <User size={20} />
          </div>
          <div className="segment-info">
            <span className="segment-label">
              {t('routeResults.sidewalkLabel')}
              {routeData.segments[2]?.distance
                ? ` (${formatDistance(routeData.segments[2].distance)})`
                : ''}
            </span>
          </div>
          <div className="route-segment-stepper">
            <button
              type="button"
              className="route-segment-stepper-btn"
              onClick={() => handleAdjustment('sidewalk', -1)}
              disabled={isOptimizing}
              aria-label={t('routeResults.decreaseSidewalk')}
            >
              <Minus size={20} strokeWidth={2.5} />
            </button>
            <span className="route-segment-stepper-value">{formatDuration(adjustments.sidewalk)}</span>
            <button
              type="button"
              className="route-segment-stepper-btn"
              onClick={() => handleAdjustment('sidewalk', 1)}
              disabled={isOptimizing}
              aria-label={t('routeResults.increaseSidewalk')}
            >
              <Plus size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="route-actions">
        <button 
          className="btn btn-secondary" 
          onClick={handleTogglePreviousRoute}
          disabled={!hasComparableAlternate || !comparisonRoute || comparisonRoute.coordinates.length === 0}
        >
          <ArrowLeft size={20} />
          <span>{showingPreviousRoute ? (t('routeResults.currentRoute') || 'Current route') : (t('routeResults.previousRoute') || 'Previous route')}</span>
        </button>
        <button 
          className="btn btn-primary" 
          onClick={() => onStartRoute(routeData.coordinates)}
        >
          <Send size={20} />
          <span>{t('button.start')}</span>
        </button>
      </div>

      <MapExplainerModal
        isOpen={showMapExplainer}
        onClose={() => setShowMapExplainer(false)}
      />
    </div>
  );
};

export default RouteResultsPage;
